import { z } from 'zod';
import { analysisMapPointKinds, collectibleKinds, importStates, resourceNodeKinds, resourcePurities, resourceTypes, resourceUsageStates, } from '../types.js';
const finiteNumber = z.number().finite();
const nonEmptyString = z.string().trim().min(1);
const localPngIconPath = z
    .string()
    .regex(/^\/icons\/[a-z0-9-]+\.png$/u);
export const mercerSphereReferenceSchema = z
    .object({
    id: nonEmptyString,
    x: finiteNumber,
    y: finiteNumber,
    z: finiteNumber,
    level: nonEmptyString.optional(),
    region: nonEmptyString.optional(),
    cave: z.boolean().optional(),
})
    .strict();
export const mercerSphereReferenceListSchema = z.array(mercerSphereReferenceSchema);
export const worldBoundsSchema = z
    .object({
    minX: finiteNumber,
    maxX: finiteNumber,
    minY: finiteNumber,
    maxY: finiteNumber,
})
    .strict()
    .superRefine((bounds, context) => {
    if (bounds.maxX <= bounds.minX) {
        context.addIssue({
            code: 'custom',
            message: 'maxX must be greater than minX',
            path: ['maxX'],
        });
    }
    if (bounds.maxY <= bounds.minY) {
        context.addIssue({
            code: 'custom',
            message: 'maxY must be greater than minY',
            path: ['maxY'],
        });
    }
});
export const mapProjectionSchema = z
    .object({
    invertX: z.boolean(),
    invertY: z.boolean(),
})
    .strict();
export const mapConfigurationFileSchema = z
    .object({
    imageWidth: z.number().int().positive(),
    imageHeight: z.number().int().positive(),
    worldBounds: worldBoundsSchema,
    projection: mapProjectionSchema.default({
        invertX: false,
        invertY: true,
    }),
})
    .strict();
export const mapConfigurationSchema = mapConfigurationFileSchema.extend({
    imagePath: nonEmptyString,
});
export const playerPositionSchema = z
    .object({
    id: nonEmptyString,
    x: finiteNumber,
    y: finiteNumber,
    z: finiteNumber,
})
    .strict();
export const playerInventoryItemSchema = z
    .object({
    itemClass: nonEmptyString,
    label: nonEmptyString,
    quantity: z.number().int().positive(),
    stacks: z.number().int().positive(),
})
    .strict();
export const playerSnapshotSchema = playerPositionSchema.extend({
    name: nonEmptyString.default('Unbekannter Spieler'),
    inventorySlots: z.number().int().nonnegative().default(0),
    occupiedInventorySlots: z.number().int().nonnegative().default(0),
    inventory: z.array(playerInventoryItemSchema).default([]),
    equipment: z.array(playerInventoryItemSchema).default([]),
    todoList: z.string().trim().min(1).optional(),
});
export const playerMapMarkerSchema = z
    .object({
    id: nonEmptyString,
    name: nonEmptyString,
    categoryName: nonEmptyString.optional(),
    markerType: nonEmptyString,
    iconId: z.number().int().nonnegative(),
    color: z.string().regex(/^#[0-9a-f]{6}$/iu),
    x: finiteNumber,
    y: finiteNumber,
    z: finiteNumber,
    compassViewDistance: nonEmptyString.optional(),
})
    .strict();
export const collectibleMapFeatureSchema = z
    .object({
    id: nonEmptyString,
    kind: z.enum(collectibleKinds),
    x: finiteNumber,
    y: finiteNumber,
    z: finiteNumber,
    level: nonEmptyString.optional(),
})
    .strict();
export const resourceNodeReferenceSchema = z
    .object({
    id: nonEmptyString,
    kind: z.enum(resourceNodeKinds),
    x: finiteNumber,
    y: finiteNumber,
    z: finiteNumber,
    level: nonEmptyString.optional(),
    resourceType: z.enum(resourceTypes).optional(),
    purity: z.enum(resourcePurities).optional(),
})
    .strict();
export const resourceNodeFeatureSchema = resourceNodeReferenceSchema.extend({
    usage: z.enum(resourceUsageStates),
    extractorIds: z.array(nonEmptyString),
});
export const resourceExtractorLinkSchema = z
    .object({
    extractorId: nonEmptyString,
    targetResourceId: nonEmptyString,
})
    .strict();
export const resourceNodeCatalogEntrySchema = z
    .object({
    id: nonEmptyString,
    resourceType: z.enum(resourceTypes),
    purity: z.enum(resourcePurities).optional(),
})
    .strict();
export const resourceNodeCatalogSchema = z
    .object({
    schemaVersion: z.literal(1),
    gameVersion: nonEmptyString,
    generatedAt: z.string().datetime({ offset: true }),
    sourceUrl: z.string().url(),
    entries: z.array(resourceNodeCatalogEntrySchema).min(1),
})
    .strict();
export const collectibleLayerStatusSchema = z
    .object({
    kind: z.enum(collectibleKinds),
    label: nonEmptyString,
    pluralLabel: nonEmptyString,
    iconPath: localPngIconPath,
    color: z.string().regex(/^#[0-9a-f]{6}$/iu),
    total: z.number().int().nonnegative(),
    collected: z.number().int().nonnegative(),
    remainingCount: z.number().int().nonnegative(),
    remaining: z.array(collectibleMapFeatureSchema),
})
    .strict()
    .superRefine((layer, context) => {
    if (layer.remainingCount !== layer.remaining.length) {
        context.addIssue({
            code: 'custom',
            message: 'remainingCount must equal remaining length',
            path: ['remainingCount'],
        });
    }
    if (layer.total !== layer.collected + layer.remainingCount) {
        context.addIssue({
            code: 'custom',
            message: 'total must equal collected plus remainingCount',
            path: ['total'],
        });
    }
    if (layer.remaining.some((feature) => feature.kind !== layer.kind)) {
        context.addIssue({
            code: 'custom',
            message: 'every remaining feature must match the layer kind',
            path: ['remaining'],
        });
    }
});
export const resourceLayerStatusSchema = z
    .object({
    kind: z.enum(resourceNodeKinds),
    label: nonEmptyString,
    pluralLabel: nonEmptyString,
    iconPath: localPngIconPath,
    color: z.string().regex(/^#[0-9a-f]{6}$/iu),
    total: z.number().int().nonnegative(),
    used: z.number().int().nonnegative(),
    unused: z.number().int().nonnegative(),
    unknown: z.number().int().nonnegative(),
    nodes: z.array(resourceNodeFeatureSchema),
})
    .strict()
    .superRefine((layer, context) => {
    if (layer.total !== layer.nodes.length ||
        layer.total !== layer.used + layer.unused + layer.unknown) {
        context.addIssue({
            code: 'custom',
            message: 'resource totals must match nodes and usage counters',
            path: ['total'],
        });
    }
    if (layer.nodes.some((feature) => feature.kind !== layer.kind)) {
        context.addIssue({
            code: 'custom',
            message: 'every resource node must match the layer kind',
            path: ['nodes'],
        });
    }
});
export const collectibleLayerDefinitionSchema = z
    .object({
    kind: z.enum(collectibleKinds),
    label: nonEmptyString,
    pluralLabel: nonEmptyString,
    expectedTotal: z.number().int().positive(),
    typePaths: z.array(nonEmptyString).min(1),
    iconPath: localPngIconPath,
    color: z.string().regex(/^#[0-9a-f]{6}$/iu),
    sourceUrl: z.string().url(),
})
    .strict();
export const resourceLayerDefinitionSchema = z
    .object({
    kind: z.enum(resourceNodeKinds),
    label: nonEmptyString,
    pluralLabel: nonEmptyString,
    typePaths: z.array(nonEmptyString).min(1),
    iconPath: localPngIconPath,
    color: z.string().regex(/^#[0-9a-f]{6}$/iu),
    sourceUrl: z.string().url(),
})
    .strict();
export const mapLayerCatalogSchema = z
    .object({
    schemaVersion: z.literal(1),
    gameVersion: nonEmptyString,
    generatedAt: z.string().datetime({ offset: true }),
    collectibles: z.array(collectibleLayerDefinitionSchema).min(1),
    resources: z.array(resourceLayerDefinitionSchema).min(1),
})
    .strict();
const isoDateTime = z.string().datetime({ offset: true });
const nonnegativeFiniteNumber = finiteNumber.nonnegative();
export const analysisMapPointSchema = z
    .object({
    id: nonEmptyString,
    kind: z.enum(analysisMapPointKinds),
    label: nonEmptyString,
    typePath: nonEmptyString,
    x: finiteNumber,
    y: finiteNumber,
    z: finiteNumber,
    level: nonEmptyString.optional(),
    circuitId: z.number().int().nonnegative().optional(),
    consumptionMw: nonnegativeFiniteNumber.optional(),
    productionCapacityMw: nonnegativeFiniteNumber.optional(),
    recipe: nonEmptyString.optional(),
    producing: z.boolean().optional(),
    clockSpeedPercent: nonnegativeFiniteNumber.optional(),
    productivityPercent: nonnegativeFiniteNumber.max(100).optional(),
})
    .strict();
export const namedCountSchema = z
    .object({
    id: nonEmptyString,
    label: nonEmptyString,
    count: z.number().int().nonnegative(),
})
    .strict();
export const powerDeviceGroupSchema = namedCountSchema.extend({
    consumptionMw: nonnegativeFiniteNumber,
    productionCapacityMw: nonnegativeFiniteNumber,
});
export const powerCircuitSnapshotSchema = z
    .object({
    id: z.number().int().nonnegative(),
    connectionCount: z.number().int().nonnegative(),
    buildingCount: z.number().int().nonnegative(),
    consumerCount: z.number().int().nonnegative(),
    generatorCount: z.number().int().nonnegative(),
    generatorsWithSerializedCapacity: z.number().int().nonnegative(),
    consumptionMw: nonnegativeFiniteNumber,
    productionCapacityMw: nonnegativeFiniteNumber,
})
    .strict();
export const powerAnalysisSchema = z
    .object({
    circuitCount: z.number().int().nonnegative(),
    connectedBuildingCount: z.number().int().nonnegative(),
    consumerCount: z.number().int().nonnegative(),
    generatorCount: z.number().int().nonnegative(),
    generatorsWithSerializedCapacity: z.number().int().nonnegative(),
    consumptionMw: nonnegativeFiniteNumber,
    productionCapacityMw: nonnegativeFiniteNumber,
    circuits: z.array(powerCircuitSnapshotSchema),
    deviceGroups: z.array(powerDeviceGroupSchema),
    consumers: z.array(analysisMapPointSchema),
    generators: z.array(analysisMapPointSchema),
})
    .strict();
export const factoryMachineGroupSchema = namedCountSchema.extend({
    producing: z.number().int().nonnegative(),
    idle: z.number().int().nonnegative(),
    configured: z.number().int().nonnegative(),
    consumptionMw: nonnegativeFiniteNumber,
    averageClockSpeedPercent: nonnegativeFiniteNumber,
    averageProductivityPercent: nonnegativeFiniteNumber.max(100).nullable(),
});
export const factoryAnalysisSchema = z
    .object({
    total: z.number().int().nonnegative(),
    configured: z.number().int().nonnegative(),
    producing: z.number().int().nonnegative(),
    idle: z.number().int().nonnegative(),
    consumptionMw: nonnegativeFiniteNumber,
    averageClockSpeedPercent: nonnegativeFiniteNumber,
    averageProductivityPercent: nonnegativeFiniteNumber.max(100).nullable(),
    machineGroups: z.array(factoryMachineGroupSchema),
    recipes: z.array(namedCountSchema),
    machines: z.array(analysisMapPointSchema),
})
    .strict();
const railCoordinateShape = {
    x: finiteNumber,
    y: finiteNumber,
    z: finiteNumber,
};
export const railTrackSegmentSchema = z.object({
    id: nonEmptyString,
    graphId: z.number().int().nullable(),
    lengthMeters: z.number().nonnegative().finite(),
    points: z.array(z.object(railCoordinateShape).strict()).min(2),
}).strict();
export const railSignalSnapshotSchema = z.object({
    id: nonEmptyString,
    type: z.enum(['block', 'path']),
    guardedTrackIds: z.array(nonEmptyString),
    observedTrackIds: z.array(nonEmptyString),
    ...railCoordinateShape,
}).strict();
export const railFreightPlatformSnapshotSchema = z.object({
    id: nonEmptyString,
    kind: z.enum(['freight', 'fluid', 'empty']).default('freight'),
    mode: z.enum(['load', 'unload']).nullable(),
    loadRate: z.number().nonnegative().finite(),
    unloadRate: z.number().nonnegative().finite(),
    ...railCoordinateShape,
}).strict();
export const railStationSnapshotSchema = z.object({
    id: nonEmptyString,
    identifierId: nonEmptyString,
    name: nonEmptyString,
    trackId: nonEmptyString.nullable(),
    currentDockDurationSeconds: z.number().nonnegative().finite().nullable(),
    platforms: z.array(railFreightPlatformSnapshotSchema),
    ...railCoordinateShape,
}).strict();
export const railDockingRuleSchema = z.object({
    definition: z.enum(['once', 'fully', 'unknown']),
    dockForSeconds: z.number().nonnegative().finite().nullable(),
    durationAndRule: z.boolean(),
    loadFilters: z.array(nonEmptyString),
    unloadFilters: z.array(nonEmptyString),
}).strict();
export const railScheduleStopSchema = z.object({
    stationIdentifierId: nonEmptyString,
    stationId: nonEmptyString.nullable(),
    stationName: nonEmptyString,
    rule: railDockingRuleSchema,
}).strict();
export const railTrainSnapshotSchema = z.object({
    id: nonEmptyString,
    name: nonEmptyString,
    locomotiveId: nonEmptyString.nullable(),
    selfDriving: z.boolean(),
    currentStopIndex: z.number().int().nonnegative().nullable(),
    stops: z.array(railScheduleStopSchema),
    routeTrackIds: z.array(nonEmptyString),
    ...railCoordinateShape,
}).strict();
export const logisticsAnalysisSchema = z
    .object({
    conveyorBelts: z.number().int().nonnegative(),
    conveyorLifts: z.number().int().nonnegative(),
    pipelines: z.number().int().nonnegative(),
    hyperTubes: z.number().int().nonnegative(),
    powerLines: z.number().int().nonnegative(),
    railwayTracks: z.number().int().nonnegative(),
    trainStations: z.number().int().nonnegative(),
    freightPlatforms: z.number().int().nonnegative(),
    trains: z.number().int().nonnegative(),
    locomotives: z.number().int().nonnegative(),
    freightWagons: z.number().int().nonnegative(),
    roadVehicles: z.number().int().nonnegative(),
    truckStations: z.number().int().nonnegative(),
    dronePorts: z.number().int().nonnegative(),
    drones: z.number().int().nonnegative(),
    points: z.array(analysisMapPointSchema),
    railTracks: z.array(railTrackSegmentSchema).default([]),
    railSignals: z.array(railSignalSnapshotSchema).default([]),
    railStations: z.array(railStationSnapshotSchema).default([]),
    railTrains: z.array(railTrainSnapshotSchema).default([]),
})
    .strict();
export const progressAnalysisSchema = z
    .object({
    currentPhase: z.number().int().nonnegative().nullable(),
    targetPhase: z.number().int().nonnegative().nullable(),
    purchasedSchematics: z.number().int().nonnegative(),
    lastActiveSchematic: nonEmptyString.nullable(),
    sinkCoupons: z.number().int().nonnegative(),
    sinkPoints: z.array(z.number().int().nonnegative()),
    sinkLevels: z.array(z.number().int().nonnegative()),
})
    .strict();
export const counterAnalysisSchema = z
    .object({
    total: z.number().int().nonnegative(),
    top: z.array(namedCountSchema),
})
    .strict();
export const constructionAnalysisSchema = z
    .object({
    currentBuildables: z.number().int().nonnegative(),
    totalBuilt: z.number().int().nonnegative(),
    totalDismantled: z.number().int().nonnegative(),
    topCurrent: z.array(namedCountSchema),
})
    .strict();
export const gameplayAnalysisSchema = z
    .object({
    creaturesKilled: counterAnalysisSchema,
    consumablesConsumed: counterAnalysisSchema,
    manuallyCrafted: counterAnalysisSchema,
    itemsPickedUp: counterAnalysisSchema,
})
    .strict();
export const worldAnalysisSchema = z
    .object({
    version: z.literal(2),
    available: z.boolean(),
    playDurationSeconds: z.number().int().nonnegative(),
    objectCount: z.number().int().nonnegative(),
    buildableObjectCount: z.number().int().nonnegative(),
    power: powerAnalysisSchema,
    factories: factoryAnalysisSchema,
    logistics: logisticsAnalysisSchema,
    progress: progressAnalysisSchema,
    construction: constructionAnalysisSchema,
    gameplay: gameplayAnalysisSchema,
    limitations: z.array(nonEmptyString),
})
    .strict();
export const persistedWorldStateSchema = z
    .object({
    schemaVersion: z.literal(3),
    generatedAt: isoDateTime,
    lastSuccessfulParseAt: isoDateTime,
    saveModifiedAt: isoDateTime,
    saveName: nonEmptyString,
    saveSize: z.number().int().nonnegative(),
    saveGameTimestamp: isoDateTime.nullable(),
    sessionName: z.string().nullable(),
    gameVersion: nonEmptyString,
    total: z.number().int().nonnegative(),
    collected: z.number().int().nonnegative(),
    remaining: mercerSphereReferenceListSchema,
    unknownCollectedPaths: z.array(nonEmptyString),
    players: z.array(playerSnapshotSchema),
    mapMarkers: z.array(playerMapMarkerSchema).default([]),
    collectibleLayers: z.array(collectibleLayerStatusSchema),
    resourceLayers: z.array(resourceLayerStatusSchema),
    analysis: worldAnalysisSchema,
})
    .strict()
    .superRefine((state, context) => {
    if (state.total !== state.collected + state.remaining.length) {
        context.addIssue({
            code: 'custom',
            message: 'total must equal collected plus remaining count',
            path: ['total'],
        });
    }
});
export const legacyV2PersistedWorldStateSchema = z
    .object({
    schemaVersion: z.literal(2),
    generatedAt: isoDateTime,
    lastSuccessfulParseAt: isoDateTime,
    saveModifiedAt: isoDateTime,
    saveName: nonEmptyString,
    saveSize: z.number().int().nonnegative(),
    saveGameTimestamp: isoDateTime.nullable(),
    sessionName: z.string().nullable(),
    gameVersion: nonEmptyString,
    total: z.number().int().nonnegative(),
    collected: z.number().int().nonnegative(),
    remaining: mercerSphereReferenceListSchema,
    unknownCollectedPaths: z.array(nonEmptyString),
    players: z.array(playerSnapshotSchema),
    mapMarkers: z.array(playerMapMarkerSchema).default([]),
    collectibleLayers: z.array(collectibleLayerStatusSchema),
    resourceLayers: z.array(resourceLayerStatusSchema),
})
    .strict();
export const legacyPersistedWorldStateSchema = z
    .object({
    schemaVersion: z.literal(1),
    generatedAt: isoDateTime,
    lastSuccessfulParseAt: isoDateTime,
    saveModifiedAt: isoDateTime,
    saveName: nonEmptyString,
    saveSize: z.number().int().nonnegative(),
    saveGameTimestamp: isoDateTime.nullable(),
    sessionName: z.string().nullable(),
    gameVersion: nonEmptyString,
    total: z.number().int().nonnegative(),
    collected: z.number().int().nonnegative(),
    remaining: mercerSphereReferenceListSchema,
    unknownCollectedPaths: z.array(nonEmptyString),
    players: z.array(playerPositionSchema),
})
    .strict();
export const publicImportErrorSchema = z
    .object({
    category: nonEmptyString,
    message: nonEmptyString,
    occurredAt: isoDateTime,
})
    .strict();
export const backendStatusSchema = z
    .object({
    lastCheckedAt: isoDateTime.nullable(),
    lastSuccessfulParseAt: isoDateTime.nullable(),
    saveModifiedAt: isoDateTime.nullable(),
    saveGameTimestamp: isoDateTime.nullable(),
    saveName: z.string().nullable(),
    sessionName: z.string().nullable(),
    gameVersion: z.string(),
    state: z.enum(importStates),
    error: publicImportErrorSchema.nullable(),
})
    .strict();
export const mercerSpheresResponseSchema = z
    .object({
    generatedAt: isoDateTime,
    saveModifiedAt: isoDateTime,
    total: z.number().int().nonnegative(),
    collected: z.number().int().nonnegative(),
    remainingCount: z.number().int().nonnegative(),
    remaining: mercerSphereReferenceListSchema,
    unknownCollectedPaths: z.array(nonEmptyString),
    players: z.array(playerSnapshotSchema),
})
    .strict();
export const mapFeaturesResponseSchema = z
    .object({
    generatedAt: isoDateTime,
    saveModifiedAt: isoDateTime,
    collectibleLayers: z.array(collectibleLayerStatusSchema),
    resourceLayers: z.array(resourceLayerStatusSchema),
    players: z.array(playerSnapshotSchema),
    mapMarkers: z.array(playerMapMarkerSchema),
    limitations: z.array(nonEmptyString),
})
    .strict();
export const worldAnalysisResponseSchema = z
    .object({
    generatedAt: isoDateTime,
    saveModifiedAt: isoDateTime,
    analysis: worldAnalysisSchema,
})
    .strict();
//# sourceMappingURL=index.js.map